import i18n from 'i18n';
import uniq from 'lodash-es/uniq';

import type {
  ConversationInsightsData,
  ConversationInsightsTheme,
  ConversationInsightsTopic,
  ExecutiveSummaryData,
  InsightData,
  SessionExplorationData,
  SessionExplorationTopic,
} from '../eventsReport';
import type {
  KnownReportMetricKey,
  NatalieReportGeneration,
  RawInsightGroup,
  RawQuote,
  RawReportInsight,
  RawTheme,
  ReportDataBlob,
  ReportDataVersion,
  ReportStatus,
  ReportingApiReport,
} from './types';
import type { InsightsReportKeyMetric } from 'pages/InsightsReport/types';
import type { TagDemographic } from 'tags';

/**
 * reporting-api never returns a status (see `ReportingApiReport`), so `completedAt` — set on the
 * first successful data upload — is the only signal. A failed generation is therefore
 * indistinguishable from one still in flight; both read as `processing`.
 */
export const deriveReportStatus = ({ completedAt }: Pick<ReportingApiReport, 'completedAt'>): ReportStatus =>
  completedAt ? 'completed' : 'processing';

/**
 * `average_session_length` → `Average Session Length`. Underscores, hyphens and camelCase
 * boundaries all become spaces so a key the pipeline invents later still reads as a label rather
 * than as a database column.
 */
export const toMetricTitle = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Natalie's `active_participants` counts conversations, not people: it is how many conversations
 * were long enough to analyse, which is exactly the set the report is built from. So it carries
 * Natalie's own wording rather than the Hosted Natter "Active Participants" label it inherited.
 */
const NATALIE_CONVERSATIONS = {
  title: i18n.t('insightReport:natalieReport.participantMetrics.conversationsTitle'),
  tooltip: i18n.t('insightReport:natalieReport.participantMetrics.conversationsTooltip'),
};

/**
 * Curated presentation for the keys prod reports carry today: the wording each number has earned,
 * plus the tooltip that explains what it counts. Anything not listed still renders — it just gets
 * a derived title and no tooltip, since inventing an explanation for a metric this app has never
 * seen would be worse than omitting one.
 */
const KNOWN_METRICS: Record<KnownReportMetricKey, Omit<InsightsReportKeyMetric, 'key' | 'value'>> = {
  active_participants: NATALIE_CONVERSATIONS,
  /** insights-backend renamed `active_participants` to this; a blob carries one or the other, never both. */
  natalie_conversations: NATALIE_CONVERSATIONS,
};

const isKnownMetric = (key: string): key is KnownReportMetricKey => key in KNOWN_METRICS;

/**
 * Never rendered. Hidden here rather than in the pipeline because report blobs are immutable once
 * written to S3: suppressing the card at render time also clears it from every report already generated.
 * - `natters_facilitated` counts everyone who opened Natalie and started talking, short conversations
 *   included — so it renders as a second, larger conversation count beside the one the report is
 *   actually built from, which is how it read to clients as an anomaly.
 * - `conversation_rating` has no data source and is always 0; insights-backend stops sending it, but
 *   older blobs still carry it.
 */
const HIDDEN_METRIC_KEYS = new Set<string>(['natters_facilitated', 'conversation_rating']);

/**
 * Every entry in `metrics` becomes a card, in the order the pipeline emitted them, bar the ones
 * deliberately hidden. Non-finite values are dropped rather than rendered as `NaN`, so a
 * partially-written metrics object degrades to the cards that do have numbers.
 */
export const mapBlobToKeyMetrics = (blob: ReportDataBlob | undefined): InsightsReportKeyMetric[] =>
  Object.entries(blob?.metrics ?? {})
    .filter(([key, value]) => !HIDDEN_METRIC_KEYS.has(key) && typeof value === 'number' && Number.isFinite(value))
    .map(([key, value]) => ({
      key,
      value,
      ...(isKnownMetric(key) ? KNOWN_METRICS[key] : { title: toMetricTitle(key) }),
    }));

const isParseableDate = (value: unknown): value is string =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value));

/**
 * The version list, newest first. reporting-api does not promise an order, and the version history
 * panel reads top to bottom from the latest. An entry without an id or a parseable date cannot be labelled or
 * selected, so it is dropped rather than shown as "Invalid Date".
 */
export const mapReportDataVersions = (response: unknown): ReportDataVersion[] => {
  const versions = (response as { versions?: unknown } | undefined)?.versions;

  if (!Array.isArray(versions)) throw new Error('Invalid report versions response');

  return (versions as Partial<ReportDataVersion>[])
    .filter(
      (version): version is ReportDataVersion =>
        typeof version?.versionId === 'string' && isParseableDate(version.lastModified)
    )
    .map(({ versionId, lastModified, isLatest, size }) => ({
      versionId,
      lastModified,
      isLatest: isLatest === true,
      ...(typeof size === 'number' ? { size } : {}),
    }))
    .sort((a, b) => Date.parse(b.lastModified) - Date.parse(a.lastModified));
};

/** `undefined` for versions generated before the block existed; the header then falls back. */
export const mapBlobToGeneration = (blob: ReportDataBlob | undefined): NatalieReportGeneration | undefined => {
  const generation = blob?.generation;

  if (!generation || !isParseableDate(generation.generatedAt)) return undefined;

  const { generatedAt, conversationCount } = generation;

  return {
    generatedAt,
    ...(typeof conversationCount === 'number' && Number.isFinite(conversationCount) ? { conversationCount } : {}),
  };
};

const quoteTexts = (quotes: RawQuote[] = []): string[] => quotes.map(({ text }) => text);

const quoteAuthors = (quotes: RawQuote[] = []): number[] => quotes.map(({ user }) => user);

/**
 * The pipeline writes `updatedAt` as epoch seconds with sub-second precision
 * (`"1785522287.594022412"`). Anything non-numeric is passed through untouched so an
 * already-ISO value from a future pipeline version survives rather than becoming `Invalid Date`.
 */
const toIsoTimestamp = (updatedAt: string): string => {
  const seconds = Number(updatedAt);

  if (!updatedAt || Number.isNaN(seconds)) return updatedAt;

  return new Date(Math.round(seconds * 1000)).toISOString();
};

export const mapBlobToExecutiveSummary = (blob: ReportDataBlob | undefined): ExecutiveSummaryData | undefined => {
  const summary = blob?.executiveSummary;

  if (!summary) return undefined;

  return {
    content: summary.content ?? '',
    author: summary.author,
    updatedAt: toIsoTimestamp(summary.updatedAt),
    isPublished: summary.isPublished,
  };
};

const mapThemeToConversationInsightsTheme = (theme: RawTheme): ConversationInsightsTheme => ({
  title: theme.name,
  participantAttribute: theme.participation.percentage.value,
  insights: (theme.insights ?? []).map(({ headline, explanation, keyQuotes, actions }) => ({
    title: headline,
    description: explanation,
    quotes: quoteTexts(keyQuotes),
    suggestionActions: {
      title: actions.summary,
      actions: actions.actions,
      quotes: quoteTexts(actions.keyQuotes),
    },
  })),
});

const mapInsightToConversationInsightsTopic = (insight: RawReportInsight): ConversationInsightsTopic => ({
  title: insight.question.question,
  themes: (insight.themes ?? []).map(mapThemeToConversationInsightsTheme),
});

export const mapBlobToConversationInsights = (blob: ReportDataBlob | undefined): ConversationInsightsData => ({
  topics: (blob?.insights ?? []).map(mapInsightToConversationInsightsTopic),
});

const isValidParticipantUserId = (userId: number): boolean => Number.isSafeInteger(userId) && userId > 0;

const mapParticipantUserIds = (userIds: number[] | undefined): number[] | undefined => {
  if (userIds === undefined) return undefined;

  if (!Array.isArray(userIds) || userIds.some((userId) => !isValidParticipantUserId(userId))) {
    throw new Error('Invalid report participant list');
  }

  return uniq(userIds);
};

/** Explicit participant lists support reach; quote authors only support quote filtering. */
const mapGroupToInsightData = (
  group: RawTheme | RawInsightGroup,
  topicParticipants: Set<number> | undefined
): InsightData => {
  const participatingUsers = mapParticipantUserIds(group.participation.participatingUsers);

  // Shared hosted reach assumes each group's participants belong to the topic denominator.
  if (topicParticipants && participatingUsers?.some((userId) => !topicParticipants.has(userId))) {
    throw new Error('Report group participants must belong to the topic cohort');
  }

  return {
    title: group.name,
    description: group.description,
    participation: group.participation.percentage.value,
    participationFilterable: topicParticipants !== undefined && participatingUsers !== undefined,
    quotes: quoteTexts(group.keyQuotes),
    userQuotes: (group.keyQuotes ?? []).map(({ text, user }) => ({ quote: text, user })),
    contributingUsers: participatingUsers ?? uniq(quoteAuthors(group.keyQuotes)),
  };
};

/**
 * Every author appearing anywhere under a question — group quotes plus the quotes nested on
 * each theme insight and its suggested actions. Only used for reports that predate the
 * pipeline emitting `contributingUsers` (before ~Oct 2025).
 */
const deriveTopicContributingUsers = (insight: RawReportInsight): number[] => {
  const groupAuthors = [
    ...(insight.themes ?? []),
    ...(insight.categories ?? []),
    ...(insight.subCategories ?? []),
  ].flatMap((group) => quoteAuthors(group.keyQuotes));

  const nestedAuthors = (insight.themes ?? [])
    .flatMap((theme) => theme.insights ?? [])
    .flatMap((themeInsight) => [
      ...quoteAuthors(themeInsight.keyQuotes),
      ...quoteAuthors(themeInsight.actions?.keyQuotes),
    ]);

  return uniq([...groupAuthors, ...nestedAuthors]);
};

const mapInsightToSessionExplorationTopic = (insight: RawReportInsight): SessionExplorationTopic => {
  const contributingUsers = mapParticipantUserIds(insight.contributingUsers);
  const topicParticipants = contributingUsers === undefined ? undefined : new Set(contributingUsers);

  return {
    title: insight.question.question,
    conversationSummary: insight.summary,
    themes: (insight.themes ?? []).map((group) => mapGroupToInsightData(group, topicParticipants)),
    categories: (insight.categories ?? []).map((group) => mapGroupToInsightData(group, topicParticipants)),
    subCategories: (insight.subCategories ?? []).map((group) => mapGroupToInsightData(group, topicParticipants)),
    contributingUsers: contributingUsers ?? deriveTopicContributingUsers(insight),
    // Natalie deliberately does not apply community anonymity thresholds until backend consolidation.
    anonymityFilterEnabled: false,
  };
};

export const mapBlobToSessionExploration = (blob: ReportDataBlob | undefined): SessionExplorationData => ({
  topics: (blob?.insights ?? []).map(mapInsightToSessionExplorationTopic),
});

/** Only the pipeline's complete contributor lists define the report's tag-query cohort. */
export const mapBlobToTagUserIds = (blob: ReportDataBlob | undefined): string[] | undefined => {
  if (!Array.isArray(blob?.insights)) return undefined;

  const users = new Set<string>();

  for (const insight of blob.insights) {
    if (!Array.isArray(insight?.contributingUsers)) return undefined;

    for (const userId of insight.contributingUsers) {
      if (!isValidParticipantUserId(userId)) return undefined;
      users.add(String(userId));
    }
  }

  return [...users];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

/** Translate tag-api's envelope into the shared demographic UI model at the API boundary. */
export const mapReportDemographics = (response: unknown): TagDemographic[] => {
  if (!isRecord(response) || !Array.isArray(response.demographics)) {
    throw new Error('Invalid report demographics response');
  }

  return response.demographics.map((demographic: unknown) => {
    if (
      !isRecord(demographic) ||
      typeof demographic.category !== 'string' ||
      (demographic.type !== 'options' && demographic.type !== 'freetext') ||
      !Array.isArray(demographic.tags)
    ) {
      throw new Error('Invalid report demographics response');
    }

    return {
      categoryName: demographic.category,
      tags: demographic.tags.map((tag: unknown) => {
        if (!isRecord(tag) || typeof tag.value !== 'string' || !isCount(tag.count)) {
          throw new Error('Invalid report demographics response');
        }

        return { tagName: tag.value, count: tag.count };
      }),
    };
  });
};

/** A malformed response must remain an error, never an implicit unfiltered selection. */
export const mapReportFilteredUserIds = (response: unknown, userIds: string[]): number[] => {
  if (!isRecord(response) || !Array.isArray(response.members) || !isCount(response.count)) {
    throw new Error('Invalid report tag filter response');
  }

  const cohort = new Set(userIds);
  const members = response.members.map((member: unknown) => {
    if (
      !isRecord(member) ||
      typeof member.userId !== 'string' ||
      !cohort.has(member.userId) ||
      !/^[1-9]\d*$/.test(member.userId) ||
      !Number.isSafeInteger(Number(member.userId)) ||
      !isRecord(member.tags) ||
      !Object.values(member.tags).every((value) => typeof value === 'string')
    ) {
      throw new Error('Invalid report tag filter response');
    }

    return Number(member.userId);
  });

  return uniq(members);
};
